import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import {
  DiseasesOnPlotResult,
  GenericSoilQuestionsWithStatus,
  PotatoDiseases,
  ProcessedAnswers,
  QuestionnaireAnswerValue,
} from 'src/app/tga/shared/models/models';

import { selectGenericSoilQuestions } from 'src/app/tga/store/tga.selectors';

import {
  DepthStorendeLaag,
  GrasslandOrGrassSeed,
  MainSoilTillageMethod,
  MaterialState,
  Structuurbederfsource,
  TGACheckboxQuestion,
  TGADateYearQuestion,
  TGADropdownQuestion,
  TGAFreeTextQuestion,
  TgaInputType,
  TGAQuestion,
  TGARadioButtonQuestion,
  TGAWarningMessageQuestion,
} from '../../../models/question-models';

@Injectable({
  providedIn: 'root',
})
export class GenericSoilQuestionsService {
  readonly store = inject(Store);

  public static readonly isRentedField = 'IsRentedField';
  public static readonly lastHarvestedYear = 'lastHarvestedYear';
  public static readonly mainSoilTillageMethod = 'mainSoilTillageMethod';
  public static readonly isInLuwte = 'isInLuwte';
  public static readonly hadGrasslandOrGrassSeed = 'hadGrasslandOrGrassSeed';
  public static readonly greenManureCropId = 'greenManureCropId';
  public static readonly diseasesObservedOnThePlot = 'diseasesObservedOnThePlot';
  public static readonly hasSoilVariety = 'hasSoilVariety';
  public static readonly hasSoilscan = 'hasSoilscan';
  public static readonly soilVarietyDifferences = 'soilVarietyDifferences';
  public static readonly IsStuifgevoelig = 'IsStuifgevoelig';
  public static readonly structuurbederfsource = 'hasStructuurbederf';
  public static readonly depthStorendeLaag = 'depthStorendeLaag';
  public static readonly isBeredenUnderWetConditions = 'isBeredenUnderWetConditions';
  public static readonly hasWorkingDrainage = 'hasWorkingDrainage';
  public static readonly hasLaagten = 'hasLaagten';
  public static readonly hasAManalysis = 'hasAManalysis';
  public static readonly hasAaltjesAnalysis = 'hasAaltjesAnalysis';
  public static readonly isExpectedInUitgangsmateriaal = 'isExpectedInUitgangsmateriaal';
  public static readonly knolbehandelingMaterial = 'knolbehandelingMaterial';

  // Diseases observed on the plot checkbox options
  private readonly hadAaltjesDamage = 'HadAaltjesDamage';
  private readonly expectedStorageOnCropField = 'ExpectedStorageOnCropField';
  private readonly hasGewoneSchurftPressure = 'HasGewoneSchurftPressure';
  private readonly hasRhizoctoniaPressure = 'HasRhizoctoniaPressure';
  private readonly hadRitnaalden = 'HadRitnaalden';
  private readonly hasSclerotiniaPressure = 'HasSclerotiniaPressure';
  private readonly hadTabaksratelvirus = 'HadTabaksratelvirus';
  private readonly hasZwarteSpikkelPressure = 'HasZwarteSpikkelPressure';
  private readonly hasSnails = 'HasSnails';

  public soilQuestions: TGAQuestion<TgaInputType>[] = [];
  private soilQuestionsState$: Observable<GenericSoilQuestionsWithStatus | null> = of();

  processYesNoAnswer(answer: QuestionnaireAnswerValue): boolean {
    if (typeof answer !== 'string' && !Array.isArray(answer)) {
      return false;
    }

    if (Array.isArray(answer) && typeof answer[0] === 'string') {
      return (answer as string[]).includes('yes');
    }

    return answer === 'yes';
  }

  /**
   * Converts a nullable boolean state value to a question format.
   * Returns 'undefined' for null values, 'yes' for true, 'no' for false.
   */
  private convertNullableBooleanToQuestionValue(value: boolean | null): string | undefined {
    if (value === null) {
      return undefined;
    }
    return value ? 'yes' : 'no';
  }

  /**
   * Converts a nullable boolean state value to a question format with 'unknown' option.
   * Returns 'unknown' for null values, 'yes' for true, 'no' for false.
   */
  private convertNullableBooleanToQuestionValueWithUnknown(value: boolean | null): string {
    if (value === null) {
      return 'unknown';
    }
    return value ? 'yes' : 'no';
  }

  processYesNoUnknownAnswer(answer: QuestionnaireAnswerValue): boolean | null {
    if (typeof answer !== 'string' && !Array.isArray(answer)) {
      return null;
    }

    if (Array.isArray(answer) && typeof answer[0] === 'string') {
      return (answer as string[]).includes('yes');
    }

    if (answer === 'unknown' || answer === '') {
      return null;
    }

    return answer === 'yes';
  }

  processDiseasesOnThePlot(processedAnswers: ProcessedAnswers): DiseasesOnPlotResult {
    const answer = processedAnswers[GenericSoilQuestionsService.diseasesObservedOnThePlot];
    let selectedKeys: string[];
    if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'object' && 'key' in answer[0]) {
      const checkboxArray = answer as { key: string; selected: boolean }[];
      selectedKeys = checkboxArray
        .filter((item) => item.selected === true)
        .map((item) => item.key);
    } else if (Array.isArray(answer) && answer.length > 0 && typeof answer[0] === 'string') {
      // Handle string array format (backward compatibility)
      selectedKeys = answer as string[];
    } else {
      // Handle empty or invalid data
      selectedKeys = [];
    }

    return {
      hadAaltjesDamage: selectedKeys.includes(this.hadAaltjesDamage),
      expectedStorageOnCropField: selectedKeys.includes(this.expectedStorageOnCropField),
      hasGewoneSchurftPressure: selectedKeys.includes(this.hasGewoneSchurftPressure),
      hasRhizoctoniaPressure: selectedKeys.includes(this.hasRhizoctoniaPressure),
      hadRitnaalden: selectedKeys.includes(this.hadRitnaalden),
      hasSclerotiniaPressure: selectedKeys.includes(this.hasSclerotiniaPressure),
      hadTabaksratelvirus: selectedKeys.includes(this.hadTabaksratelvirus),
      hasZwarteSpikkelPressure: selectedKeys.includes(this.hasZwarteSpikkelPressure),
      hasSnails: selectedKeys.includes(this.hasSnails),
    };
  }

  processGreenManureCropId(answer: QuestionnaireAnswerValue): number | null {
    if (answer === 'None') {
      return null;
    }

    return Number(answer);
  }

  processDateYearAnswer(answer: Date | null): number | null {
    return answer ? answer.getFullYear() : null;
  }

  processIsExpectedInUitgangsmateriaal(answer: QuestionnaireAnswerValue | { key: string; selected: boolean }[]): {
    isRhizoctoniaExpected: boolean;
    isCropVarietyRhizoctoniaSensitive: boolean;
    hasZilverschurftdrukInUitgangsmateriaal: boolean;
    isFusariumExpected: boolean;
  } {
    if (!Array.isArray(answer)) {
      return {
        isRhizoctoniaExpected: false,
        isCropVarietyRhizoctoniaSensitive: false,
        hasZilverschurftdrukInUitgangsmateriaal: false,
        isFusariumExpected: false,
      }
    }

    // Handle string array case
    if (answer.length > 0 && typeof answer[0] === 'string') {
      const stringArray = answer as string[];
      return {
        isRhizoctoniaExpected: stringArray.includes('Rhizoctonia'),
        isCropVarietyRhizoctoniaSensitive: stringArray.includes('Rhizoctonia'),
        hasZilverschurftdrukInUitgangsmateriaal: stringArray.includes('Zilverschurft'),
        isFusariumExpected: stringArray.includes('Fusarium'),
      };
    }

    // Handle key-value object array case
    const keyValueArray = answer as { key: string; selected: boolean }[];
    const rhizoctoniaItem = keyValueArray.find((item) => item.key === 'Rhizoctonia');
    const zilverschurftItem = keyValueArray.find((item) => item.key === 'Zilverschurft');
    const fusariumItem = keyValueArray.find((item) => item.key === 'Fusarium');

    return {
      isRhizoctoniaExpected: rhizoctoniaItem?.selected ?? false,
      isCropVarietyRhizoctoniaSensitive: rhizoctoniaItem?.selected ?? false,
      hasZilverschurftdrukInUitgangsmateriaal: zilverschurftItem?.selected ?? false,
      isFusariumExpected: fusariumItem?.selected ?? false,
    };
  }

  initializeSoilQuestions(greenManureCrops: { key: string; value: string }[]): void {
    this.getQuestions(greenManureCrops).subscribe((questions) => {
      this.soilQuestions = questions;
    });
  }

  initValues(): TGAQuestion<TgaInputType>[] {
    this.soilQuestionsState$ = this.store.select(selectGenericSoilQuestions);
    this.soilQuestionsState$.subscribe((withStatus) => {
      const state = withStatus?.genericSoil;
      if (state) {
        for (const question of this.soilQuestions) {
          switch (question.key) {
            case GenericSoilQuestionsService.lastHarvestedYear:
              question.value = state.lastHarvestedYear ?? undefined;
              break;
            case GenericSoilQuestionsService.mainSoilTillageMethod: {
              question.value = state.mainSoilTillageMethod ? MainSoilTillageMethod[state.mainSoilTillageMethod].toString() : undefined;
              break;
            }
            case GenericSoilQuestionsService.isInLuwte:
              question.value = this.convertNullableBooleanToQuestionValue(state.isInLuwte);
              break;
            case GenericSoilQuestionsService.hadGrasslandOrGrassSeed:
              question.value = state.hadGrasslandOrGrassSeed ? GrasslandOrGrassSeed[state.hadGrasslandOrGrassSeed].toString() : undefined;
              break;
            case GenericSoilQuestionsService.greenManureCropId:
              question.value = state.greenManureCropId?.toString() || 'None';
              break;
            case GenericSoilQuestionsService.diseasesObservedOnThePlot: {
              const diseases = [
                { key: this.hadAaltjesDamage, selected: state.hadAaltjesDamage ?? false },
                { key: this.expectedStorageOnCropField, selected: state.expectedStorageOnCropField ?? false },
                { key: this.hasGewoneSchurftPressure, selected: state.hasGewoneSchurftPressure ?? false },
                { key: this.hasRhizoctoniaPressure, selected: state.hasRhizoctoniaPressure ?? false },
                { key: this.hadRitnaalden, selected: state.hadRitnaalden ?? false },
                { key: this.hasSclerotiniaPressure, selected: state.hasSclerotiniaPressure ?? false },
                { key: this.hadTabaksratelvirus, selected: state.hadTabaksratelvirus ?? false },
                { key: this.hasZwarteSpikkelPressure, selected: state.hasZwarteSpikkelPressure ?? false },
                { key: this.hasSnails, selected: state.hasSnails ?? false },
              ];
              question.value = diseases;

              break;
            }
            case GenericSoilQuestionsService.hasSoilVariety:
              question.value = this.convertNullableBooleanToQuestionValueWithUnknown(state.hasSoilVariety);
              break;
            case GenericSoilQuestionsService.soilVarietyDifferences:
              question.value = state.soilVarietyDifferences ?? undefined;
              break;
            case GenericSoilQuestionsService.hasSoilscan:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasSoilScan);
              break;
            case GenericSoilQuestionsService.IsStuifgevoelig:
              question.value = this.convertNullableBooleanToQuestionValue(state.isStuifgevoelig);
              break;
            case GenericSoilQuestionsService.structuurbederfsource:
              question.value = state.structuurbederfSource ? Structuurbederfsource[state.structuurbederfSource].toString() : undefined;
              break;
            case GenericSoilQuestionsService.depthStorendeLaag:
              question.value = state.depthStorendeLaag ? DepthStorendeLaag[state.depthStorendeLaag].toString() : undefined;
              break;
            case GenericSoilQuestionsService.hasWorkingDrainage:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasWorkingDrainage);
              break;
            case GenericSoilQuestionsService.hasLaagten:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasLaagten);
              break;
            case GenericSoilQuestionsService.isBeredenUnderWetConditions:
              question.value = this.convertNullableBooleanToQuestionValue(state.isBeredenUnderWetConditions);
              break;
            case GenericSoilQuestionsService.hasAManalysis:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasAManalysis);
              break;
            case GenericSoilQuestionsService.hasAaltjesAnalysis:
              question.value = this.convertNullableBooleanToQuestionValue(state.hasAaltjesAnalysis);
              break;
            case GenericSoilQuestionsService.isExpectedInUitgangsmateriaal: {
              const expectedItems = [
                { key: 'Rhizoctonia', selected: state.isRhizoctoniaExpected ?? false },
                { key: 'Zilverschurft', selected: state.hasZilverschurftdrukInUitgangsmateriaal ?? false },
                { key: 'Fusarium', selected: state.isFusariumExpected ?? false },
              ];
              question.value = expectedItems;
              break;
            }
            case GenericSoilQuestionsService.knolbehandelingMaterial:
              question.value = state.knolbehandelingMaterial ? MaterialState[state.knolbehandelingMaterial].toString() : undefined;
              break;
          }
        }
      }
    });

    return this.soilQuestions;
  }

  getQuestions(greenManureCrops: { key: string; value: string }[]): Observable<TGAQuestion<TgaInputType>[]> {
    let today = new Date();
    let fifteenYearsAgo = new Date();
    fifteenYearsAgo.setFullYear(today.getFullYear() - 15);


    return of([
      new TGAWarningMessageQuestion({
        key: GenericSoilQuestionsService.isRentedField,
        label: 'TGA.Questionnaire.Generic.Soil.OwnOrRentalLand.Question',
        placeholder: 'TGA.Questionnaire.Generic.Soil.WarningPlaceholder',
      }),

      new TGADateYearQuestion({
        key: GenericSoilQuestionsService.lastHarvestedYear,
        label: 'TGA.Questionnaire.Generic.Soil.LastHarvestedYear.Question',
        placeholder: 'TGA.Questionnaire.Generic.Soil.LastHarvestedYear.Placeholder',
        value: undefined,
        range: { min: fifteenYearsAgo, max: today },
        required: false,
      }),

      new TGADropdownQuestion({
        options: {
          key: GenericSoilQuestionsService.mainSoilTillageMethod,
          label: 'TGA.Questionnaire.Generic.Soil.MainTillage.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.MainTillage.Placeholder',
          order: 1,
          options: [
            {
              key: MainSoilTillageMethod.TraditioneelPloegBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.TraditionalPlowLessThan25Cm',
            },
            {
              key: MainSoilTillageMethod.TraditioneelPloegAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.TraditionalPlowMoreThan25Cm',
            },
            {
              key: MainSoilTillageMethod.SpittenBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.SpadingLessThan25Cm',
            },
            {
              key: MainSoilTillageMethod.SpittenAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.SpadingMoreThan25Cm',
            },
            {
              key: MainSoilTillageMethod.BovenoverPloegAbove.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.PloughingOver',
            },
            {
              key: MainSoilTillageMethod.BovenoverPloegBelow.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.ShallowTopPloughing',
            },
            {
              key: MainSoilTillageMethod.NietKerendeGrondbewerking.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.NonInversionTillage',
            },
            {
              key: MainSoilTillageMethod.GeenGrondbewerking.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.MainTillage.Options.NoTillage',
            },
          ],
          required: true,
        },
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.isInLuwte,
        label: 'TGA.Questionnaire.Generic.Soil.PlotInShelteredLocation.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.PlotInShelteredLocation.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.PlotInShelteredLocation.No' },
        ],
        required: true,
      }),

      new TGADropdownQuestion({
        options: {
          key: GenericSoilQuestionsService.hadGrasslandOrGrassSeed,
          label: 'TGA.Questionnaire.Generic.Soil.HadGrasslandOrGrassSeed.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Placeholder',
          order: 1,
          options: [
            {
              key: GrasslandOrGrassSeed.YesLast5Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.YesInTheLast5Years',
            },
            {
              key: GrasslandOrGrassSeed.Yes5To10Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.Yes5To10YearsAgo',
            },
            {
              key: GrasslandOrGrassSeed.YesMoreThan10Years.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.YesMoreThan10YearsAgo',
            },
            {
              key: GrasslandOrGrassSeed.No.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.HadGrasslandOrGrassSeed.Options.No',
            },
          ],
          required: true,
        },
      }),

      new TGADropdownQuestion({
        options: {
          key: GenericSoilQuestionsService.greenManureCropId,
          label: 'TGA.Questionnaire.Generic.Soil.GreenManureCrop.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.GreenManureCrop.Placeholder',
          order: 1,
          options: [
            {
              key: 'None',
              value: 'TGA.Questionnaire.SoilQuestions.GreenManureCrop.Options.None',
            },
            ...greenManureCrops,
          ],
          required: true,
        },
      }),

      new TGACheckboxQuestion({
        key: GenericSoilQuestionsService.diseasesObservedOnThePlot,
        label: 'TGA.Questionnaire.Generic.Soil.DiseasesObservedOnThePlot.Question',
        options: [
          {
            key: this.hadAaltjesDamage,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HadAaltjesDamage',
          },
          {
            key: this.expectedStorageOnCropField,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.ExpectedStorageOnCropField',
          },
          {
            key: this.hasGewoneSchurftPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasGewoneSchurftPressure',
          },
          {
            key: this.hasRhizoctoniaPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasRhizoctoniaPressure',
          },
          {
            key: this.hadRitnaalden,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasRitnaalden',
          },
          {
            key: this.hasSclerotiniaPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasSclerotiniaPressure',
          },
          {
            key: this.hadTabaksratelvirus,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HadTabaksratelvirus',
          },
          {
            key: this.hasZwarteSpikkelPressure,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasZwarteSpikkelPressure',
          },
          {
            key: this.hasSnails,
            value: 'TGA.Questionnaire.SoilQuestions.DiseasesObservedOnThePlot.Options.HasSnails',
          },
        ],
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasSoilVariety,
        label: 'TGA.Questionnaire.Generic.Soil.SoilVariation.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.SoilVariation.Unknown' },
        ],
        required: true,
      }),

      new TGAFreeTextQuestion({
        key: GenericSoilQuestionsService.soilVarietyDifferences,
        label: 'TGA.Questionnaire.Generic.Soil.SoilVariationDifferences',
        value: undefined,
        dependsOnQuestion: {
          questionKey: GenericSoilQuestionsService.hasSoilVariety,
          questionVisibleForValues: ['yes'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasSoilscan,
        label: 'TGA.Questionnaire.Generic.Soil.WasASoilScanCarriedOut.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.WasASoilScanCarriedOut.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.WasASoilScanCarriedOut.No' },
        ],
        dependsOnQuestion: {
          questionKey: GenericSoilQuestionsService.hasSoilVariety,
          questionVisibleForValues: ['yes'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.IsStuifgevoelig,
        label: 'TGA.Questionnaire.Generic.Soil.IsThePlotSensitiveToDrift.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.IsThePlotSensitiveToDrift.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.IsThePlotSensitiveToDrift.No' },
        ],
        required: true,
      }),

      new TGADropdownQuestion({
        options: {
          key: GenericSoilQuestionsService.structuurbederfsource,
          label: 'TGA.Questionnaire.Generic.Soil.Structuurbederfsource.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Placeholder',
          order: 1,
          options: [
            {
              key: Structuurbederfsource.No.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.No',
            },
            {
              key: Structuurbederfsource.Bewerkbaarheid.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesEdited',
            },
            {
              key: Structuurbederfsource.Doorlatendheid.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesPermeability',
            },
            {
              key: Structuurbederfsource.StorendeLaag.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.Structuurbederfsource.Options.YesDisturbingLayers',
            },
          ],
          required: true,
        },
      }),

      new TGADropdownQuestion({
        options: {
          key: GenericSoilQuestionsService.depthStorendeLaag,
          label: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Question',
          placeholder: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Placeholder',
          order: 1,
          options: [
            {
              key: DepthStorendeLaag.In0to10.toString(),
              value: 'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.ZeroToTenCm',
            },
            {
              key: DepthStorendeLaag.In10to25.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.TenToTwentyFiveCm',
            },
            {
              key: DepthStorendeLaag.In25to35.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.TwentyFiveToThirtyFiveCm',
            },
            {
              key: DepthStorendeLaag.In35orMore.toString(),
              value:
                'TGA.Questionnaire.SoilQuestions.AtWhatDepthAreTheDisturbingLayersVisible.Options.MoreThanThirtyFiveCm',
            },
          ],
          dependsOnQuestion: {
            questionKey: GenericSoilQuestionsService.structuurbederfsource,
            questionVisibleForValues: [Structuurbederfsource.StorendeLaag.toString()],
          },
          required: false,
        },
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasWorkingDrainage,
        label: 'TGA.Questionnaire.Generic.Soil.IsTheDrainageContinuing.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.IsTheDrainageContinuing.Unknown' },
        ],
        dependsOnQuestion: {
          questionKey: GenericSoilQuestionsService.structuurbederfsource,
          questionVisibleForValues: ['YesPermeability'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasLaagten,
        label: 'TGA.Questionnaire.Generic.Soil.AnyDepressionsPresent.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.No' },
          { key: 'unknown', value: 'TGA.Questionnaire.SoilQuestions.AnyDepressionsPresent.Unknown' },
        ],
        dependsOnQuestion: {
          questionKey: GenericSoilQuestionsService.structuurbederfsource,
          questionVisibleForValues: ['YesPermeability'],
        },
        required: false,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.isBeredenUnderWetConditions,
        label: 'TGA.Questionnaire.Generic.Soil.PlotDrivenInWetConditions.Question',
        placeholder: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Placeholder',
        order: 1,
        options: [
          {
            key: 'yes',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.Yes',
          },
          {
            key: 'no',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.No',
          },
          {
            key: 'unknown',
            value: 'TGA.Questionnaire.SoilQuestions.PlotDrivenInWetConditions.Options.Unknown',
          },
        ],
        required: true,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasAManalysis,
        label: 'TGA.Questionnaire.Generic.Soil.HasAMAnalysis.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.HasAMAnalysis.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.HasAMAnalysis.No' },
        ],
        cropFieldSpecificResults: true,
      }),

      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.hasAaltjesAnalysis,
        label: 'TGA.Questionnaire.Generic.Soil.HasAaltjesAnalysis.Question',
        order: 3,
        options: [
          { key: 'yes', value: 'TGA.Questionnaire.SoilQuestions.HasAaltjesAnalysis.Yes' },
          { key: 'no', value: 'TGA.Questionnaire.SoilQuestions.HasAaltjesAnalysis.No' },
        ],
        required: true,
        cropFieldSpecificResults: true,
      }),

      new TGACheckboxQuestion({
        key: GenericSoilQuestionsService.isExpectedInUitgangsmateriaal,
        label: 'TGA.Questionnaire.SoilQuestions.IsRhizoctoniaExpected.Question',
        options: [
          {
            key: PotatoDiseases.Rhizoctonia,
            value: PotatoDiseases.Rhizoctonia.toString(),
          },
          {
            key: PotatoDiseases.Zilverschurft,
            value: PotatoDiseases.Zilverschurft.toString(),
          },
          {
            key: PotatoDiseases.Fusarium,
            value: PotatoDiseases.Fusarium.toString(),
          },
        ],
        required: false,
        hasInfoIcon: true,
      }),
      new TGARadioButtonQuestion({
        key: GenericSoilQuestionsService.knolbehandelingMaterial,
        label: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Question',
        order: 3,
        options: [
          {
            key: MaterialState.Liquid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Liquid',
          },
          {
            key: MaterialState.Solid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Solid',
          },
          {
            key: MaterialState.LiquidAndSolid.toString(),
            value: 'TGA.Questionnaire.SoilQuestions.KnolbehandelingMaterial.Both',
          },
        ],
        required: true,
      }),
    ]);
  }

  extractRouteParams(params: { [key: string]: string | undefined }) {
    const cropYear = Number.parseInt(params['cropYear'] || new Date().getFullYear().toString(), 10);
    return { cropYear };
  }

  validateRouteParams(params: { cropYear: number }) {
    return !Number.isNaN(params.cropYear);
  }
}
